import { ShopSelection } from '../model/product/countries-shops';
import { CoverImage, CoverType, Genre } from '../model/product/general';
import { Explicitness } from '../model/product/track-metadata';
import { Track } from '../model/product/tracks';
import { ProductType } from '../product/wizard/type/product-type-select.component';

export function requiredString(value: string): null | string {
  return value ? null : $localize`:@@Common.error.required:Required`;
}

export function requiredAgeRating(value: string): null | string {
  return value
    ? null
    : $localize`:@@Common.error.requiredIfBisacGenre:Required because of selected BISAC Genre 'Juvenile Fiction' or 'Juvenile Nonfiction'`;
}

/**
 * ISBN-13: The 13. digit is calculated with the first 12 (from the left) alternately multiplied with 1 and 3 and added
 * The difference of the sum mod 10 is the check digit
 *
 * ISBN-10: The first 9 digits are multiplied with the digit itself and added. The check digit is the sum mod 10
 * 10 is shown as X
 * @param value
 */
export function validIsbnFormat(value: string) {
  if (value.length !== 13 && value.length !== 10) {
    return $localize`:@@Common.error.isbn:ISBN must be 10 or 13 digits long.`;
  }
  const lastDigit = value.slice(-1);
  const isMod11 = value.length === 10;
  const withoutCheckDigit = value.slice(0, -1);

  let sum = 0;
  for (let i = 0; i < withoutCheckDigit.length; i++) {
    if (isMod11) {
      sum += parseInt(withoutCheckDigit[i]) * (i + 1);
    } else {
      sum += parseInt(withoutCheckDigit[i]) * (i % 2 === 0 ? 1 : 3);
    }
  }

  const checkDigit = isMod11 ? sum % 11 : (10 - (sum % 10)) % 10;
  // Check digit is correct
  if (parseInt(lastDigit) === checkDigit) {
    return null;
  }

  // Check digit is 10 => visible as X
  if (isMod11 && checkDigit === 10 && lastDigit === 'X') {
    return null;
  }

  return $localize`:@@Common.error.isbn.check-digit:ISBN check digit is invalid.`;
}
export function requiredDate(value: Date | undefined): null | string {
  return value && !Number.isNaN(value.getTime()) ? null : $localize`:@@Common.error.required:Required`;
}

export function minLength(min: number): (value: string) => null | string {
  return (value) => {
    return value === '' || value.length >= min ? null : $localize`:@@Common.error.min-length:Minimum length is ${min}.`;
  };
}

export function maxLength(max: number): (value: string) => null | string {
  return (value) => {
    return value === '' || value.length <= max ? null : $localize`:@@Common.error.max-length:Maximum length is ${max}.`;
  };
}

export function min(min: number): (value: string) => null | string {
  return (value) => {
    return value === '' || parseFloat(value.replace(',', '.')) >= min
      ? null
      : $localize`:@@Common.error.min:Minimum value is ${min}.`;
  };
}

export function max(max: number): (value: string) => null | string {
  return (value) => {
    return value === '' || parseFloat(value.replace(',', '.')) <= max
      ? null
      : $localize`:@@Common.error.max:Maximum value is ${max}.`;
  };
}

export function wholeNumber(value: string): null | string {
  if (value.includes('.') || value.includes(',')) {
    return $localize`:@@Common.error.whole-number:Has to be a whole number.`;
  }

  return null;
}

export function isNumber(value: string): null | string {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const normalizedSeparator = trimmed.replace(',', '.');
  if (Number.isNaN(Number(normalizedSeparator))) {
    return $localize`:@@Common.error.not-number:Has to be a number.`;
  }

  return null;
}

export function max2Decimals(value: string): null | string {
  if (value === '') {
    return null;
  }

  let lastIndex = value.indexOf('.');
  if (lastIndex === -1) {
    lastIndex = value.indexOf(',');
  }

  if (lastIndex === -1) {
    return null;
  }

  const numberOfFractionalDigits = value.length - lastIndex - 1;
  if (numberOfFractionalDigits <= 2) {
    return null;
  }

  return $localize`:@@Common.error.max-2-decimals:Can not have more than two decimals.`;
}

export function notEmpty(value: { length: number }): null | string {
  return value.length > 0 ? null : $localize`:@@Common.error.min-length:Minimum length is ${1}.`;
}

export function notInThePast(value: Date | undefined): null | string {
  if (value === undefined || Number.isNaN(value.getTime())) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today.getTime() <= value.getTime() ? null : $localize`:@@Common.error.in-the-past:Can not be in the past`;
}

export function notBeforePreOrderDate(value: Date | undefined, preOrderDate: Date | undefined): null | string {
  if (
    value === undefined ||
    Number.isNaN(value.getTime()) ||
    preOrderDate === undefined ||
    Number.isNaN(preOrderDate.getTime())
  ) {
    return null;
  }

  return preOrderDate.getTime() <= value.getTime()
    ? null
    : $localize`:@@product-wizard.release-date.warning.too-early:The earliest release date is your defined pre-order date.`;
}

const langsWithoutTitleCheck: string[] = [
  'ru', // Russian
  'bg', // Bulgarian
  'uk', // Ukrainian
  'be', // Belarusian
  'zh', // Chinese
  'ja', // Japanese
  'ko', // Korean
  'th', // Thai
  'he', // Hebrew
  'sr', // Serbian
];

// Make sure use didn't just input '-' or spaces etc.
export const validTitle = (title: string, language: string) => {
  if (title === '') {
    return null;
  }

  const trimmed = title.trim();
  if (trimmed.length === 0) {
    return $localize`:@@Common.validate.title-format:Please check the title.`;
  }

  // Slice to handle cases like ru_RU
  if (langsWithoutTitleCheck.includes(language.slice(0, 2))) {
    return null;
  }

  // Unicode ranges
  // '0030-0039', // Numbers
  // '0041-005A', // A-Z
  // '0061-007A', // a-z
  // '00C0-02AF', // Umlauts and other
  // '0620-064A', // arabic http://de.wikipedia.org/wiki/Unicode-Block_Arabisch
  // '066D-06D5', // arabic http://de.wikipedia.org/wiki/Unicode-Block_Arabisch
  // '0370-03FF',  //Greek and Coptic
  // '0900-097F'
  // eslint-disable-next-line no-misleading-character-class
  const valid = /[0-9A-Za-z\u00c0-\u02af\u0620-\u064a\u066d-\u06d5\u0370-\u03ff\u0900-\u097f]/u.test(trimmed);
  if (valid) {
    return null;
  }

  // 0400-04ff Cyrillic
  // 0500-052f Cyrillic Supplement
  if (/[\u0400-\u052f]/u.test(trimmed)) {
    return $localize`:@@Common.validate.title-format-cyrillic:Please choose a language with Cyrillic script.`;
  }

  return $localize`:@@Common.validate.title-format:Please check the title.`;
};

export function unequalVersionTitle(title: string, titleVersion: string): null | string {
  if (title === '') {
    return null;
  }

  return title !== titleVersion
    ? null
    : $localize`:@@Common.validate.title-equals-version:The title version must differ from the Audio Product title.`;
}

export function validTitleVersion(value: string): null | string {
  if (value.includes(')') || value.includes('(')) {
    return $localize`:@@Common.validate.title-version:The title version contains non-allowed characters.`;
  }

  return null;
}

export function validEanUpc(value: string): null | string {
  if (value.length === 13) {
    // EAN
    if (/[^\d]/.test(value)) {
      return $localize`:@@Common.validate.invalid-ean:The EAN-Code is invalid. The EAN must have 13 digits including a valid check-digit.`;
    }

    let checksum = 0;
    for (let i = 0; i < 12; i += 2) {
      checksum += parseInt(value[i], 10);
      checksum += 3 * parseInt(value[i + 1], 10);
    }
    const checkdigit = (10 - (checksum % 10)) % 10;

    if (checkdigit !== parseInt(value[12], 10)) {
      return $localize`:@@Common.validate.invalid-ean:The EAN-Code is invalid. The EAN must have 13 digits including a valid check-digit.`;
    }

    return null;
  }

  if (value.length === 12) {
    // UPC
    if (/[^\d]/.test(value)) {
      return $localize`:@@Common.validate.invalid-upc:The UPC-Code is invalid. The UPC must have 12 digits including a valid check-digit.`;
    }

    let checksum = 0;
    for (let i = 0; i < 10; i += 2) {
      checksum += 3 * parseInt(value[i], 10);
      checksum += parseInt(value[i + 1], 10);
    }
    checksum += 3 * parseInt(value[10], 10);
    const checkdigit = (10 - (checksum % 10)) % 10;

    if (checkdigit !== parseInt(value[11], 10)) {
      return $localize`:@@Common.validate.invalid-upc:The UPC-Code is invalid. The UPC must have 12 digits including a valid check-digit.`;
    }
  }

  return null;
}

export function noEmoji(value: string): null | string {
  return /[\p{Emoji_Presentation}\u{fe0f}]/u.test(value)
    ? $localize`:@@Common.validate.emoji:May not include emojis.`
    : null;
}

export function validCatalogNumber(value: string): null | string {
  return /[^a-zA-Z0-9]/.test(value)
    ? $localize`:@@Common.validate.catalog-number:Allowed characters are a-z, A-Z and 0-9.`
    : null;
}

export function validPublisher(value: string): null | string {
  return value.includes(',')
    ? $localize`:@@Common.validate.publisher-comma:May not contain a comma. Please use separate lines for multiple publishers.`
    : null;
}

export function isExplicit(value: Explicitness): null | string {
  if (value === Explicitness.EXPLICIT) {
    return null;
  }

  return $localize`:@@Common.validate.mandatory-explicit:Has to be set explicit for the given product title.`;
}

export function genreSelected(genres: (Genre | undefined)[]): null | string {
  if (genres.includes(undefined)) {
    return $localize`:@@product-wizard.data.genre-required:Please select a genre.`;
  }

  return null;
}

export function isPdfMimeType(value: unknown): value is 'application/pdf' | 'application/x-pdf' | 'application/octet' {
  return value === 'application/pdf' || value === 'application/x-pdf' || value === 'application/octet';
}

export function isJpegMimeType(value: unknown): value is 'image/jpeg' | 'image/jpg' | 'image/pjpeg' {
  return value === 'image/jpeg' || value === 'image/jpg' || value === 'image/pjpeg';
}

export function isJpeg({ file }: CoverImage): null | string {
  if (file === undefined) {
    return null;
  }

  const correctMimeType = isJpegMimeType(file.type);
  const correctSuffix = file.name.toLowerCase().endsWith('.jpg');

  if (correctMimeType && correctSuffix) {
    return null;
  }

  return $localize`:@@product-wizard.data.cover-not-jpg:Has to be a .jpg file`;
}

export function hasAiUsage({ aiUsage }: CoverImage): null | string {
  if (aiUsage) {
    return null;
  }

  return $localize`:@@product-wizard.data.cover-no-ai-usage:AI Usage required`;
}

export function isPdf(file: File): null | string {
  const correctMimeType = isPdfMimeType(file.type);
  const correctSuffix = file.name.toLowerCase().endsWith('.pdf');

  if (correctMimeType && correctSuffix) {
    return null;
  }

  return $localize`:@@product-wizard.data.cover-not-pdf:Has to be a .pdf file`;
}

export function isJpegOrPdf(file: File): null | string {
  const pdfSuffix = file.name.toLowerCase().endsWith('.pdf');
  const jpgSuffix = file.name.toLowerCase().endsWith('.jpg');
  const pdfMimeType = isPdfMimeType(file.type);
  const jpgMimeType = isJpegMimeType(file.type);

  if ((pdfSuffix && pdfMimeType) || (jpgSuffix && jpgMimeType)) {
    return null;
  }

  return $localize`:@@product-wizard.data.cover-not-jpg-or-pdf:Has to be a .jpg or .pdf file`;
}

export function hasFrontcover(images: { type: CoverType }[]): null | string {
  if (images.some((img) => img.type === CoverType.FRONT)) {
    return null;
  }

  return $localize`:@@Common.validate.no-front-cover:The front cover is missing.`;
}

export function validSize(minHeight = 0, minWidth = 0, bitmap: ImageBitmap): null | string {
  const bigEnough = bitmap.width >= minWidth && bitmap.height >= minHeight;

  if (!bigEnough) {
    return $localize`:@@product-wizard.data.cover-too-small:Has to be at least ${minWidth}x${minHeight}`;
  }

  return null;
}

export function validAspectRatio(
  productType: ProductType | undefined,
  singleTrack: boolean,
  bitmap: ImageBitmap,
): null | string {
  if (productType === 'VIDEO_BUNDLE') {
    // No aspect ratio necessary for single video
    if (singleTrack) {
      return null;
    }

    if (bitmap.height * 2 < bitmap.width) {
      return $localize`:@@product-wizard.data.cover-video-too-wide:Can not be wider than 2:1`;
    }

    return null;
  }

  if (bitmap.width !== bitmap.height) {
    return $localize`:@@product-wizard.data.cover-not-quadratic:Has to be quadratic`;
  }

  return null;
}

export function selectionAvailable(optionList: { value: string }[]): (selection: string) => null | string {
  return (selection) => {
    if (!selection) {
      return null;
    }

    if (optionList.some((option) => option.value === selection)) {
      return null;
    }

    return $localize`:@@product-wizard.validate.option-no-longer-available:The selected option is no longer available`;
  };
}

export function countrySelected(countries: unknown[]): null | string {
  return countries.length === 0
    ? $localize`:@@product-wizard.validate.no-countries:Please select at least one country`
    : null;
}

export function shopSelected({ shopSelection, allShops }: ShopSelection): null | string {
  if (allShops) {
    return null;
  }

  return shopSelection.some(
    (s) => s.download === true || s.streaming === true || s.ringtone === true || s.special === true,
  )
    ? null
    : $localize`:@@product-wizard.validate.no-shops:Please select at least one shop or service`;
}

export function trackSelected(minTrackCount = 0, tracks: unknown[]): null | string {
  if (tracks.length >= minTrackCount) {
    return null;
  }

  return minTrackCount === 1
    ? $localize`:@@product-wizard.tracklisting.error.upload-singular:Please add at least one file`
    : $localize`:@@product-wizard.tracklisting.error.upload-plural:Please add at least ${minTrackCount}:min-count: files`;
}

// Only checks format
export function isValidISRC(isrc: string) {
  if (isrc.length !== 12) {
    return false;
  }

  return /[a-zA-Z]{2}[a-zA-Z\d]{3}\d{2}\d{5}/.test(isrc);
}

export function validISRC(isrc: string) {
  if (isrc === '') {
    return null;
  }
  return isValidISRC(isrc) ? null : $localize`:@@product-wizard.tracklisting.error.isrc:Invalid ISRC`;
}

export function validNumberOfPrereleases(tracks: Track[]): null | string[] {
  let downloadCount = 0;
  let streamingCount = 0;

  tracks.forEach((t) => {
    if (t.downloadReleaseDate) {
      downloadCount++;
    }
    if (t.streamingReleaseDate) {
      streamingCount++;
    }
  });

  if (downloadCount === 0 && streamingCount === 0) {
    return [
      $localize`:@@product-wizard.release-date.error.no-dates-selected:Select at least one track for prerelease.`,
    ];
  }

  const maxNumber = tracks.length / 2;
  const tooManyDownloads = downloadCount > maxNumber;
  const tooManyStreams = streamingCount > maxNumber;

  if (!tooManyDownloads && !tooManyStreams) {
    return null;
  }

  const result: string[] = [];

  if (tooManyDownloads) {
    result.push(
      $localize`:@@product-wizard.release-date.error.too-many-dates:Max. 50% of the tracks may be released as download before the product itself is released.`,
    );
  }
  if (tooManyStreams) {
    result.push(
      $localize`:@@product-wizard.release-date.error.too-many-dates.streaming:Max. 50% of the tracks may be released as streaming before the product itself is released.`,
    );
  }

  return result;
}

export function staysInSong(snippetStart: string, songDuration: number | undefined) {
  return (snippetDuration: string) => {
    if (!songDuration) {
      return null;
    }

    const parsedStart = parseFloat(snippetStart);
    const parsedDuration = parseFloat(snippetDuration);

    if (Number.isNaN(parsedStart) || Number.isNaN(parsedDuration)) {
      return null;
    }

    if (parsedStart + parsedDuration > songDuration) {
      return $localize`:@@product-wizard.validate.exeeds-song-duration:The snippet exceeds the songs duration.`;
    }

    return null;
  };
}

export function seriesNumberOrRange(value: string): string | null {
  if (value === '') {
    return null;
  }

  if (/^\d+([.,]\d+)?$/.test(value)) {
    return null;
  }

  const matches = /^(\d+(?:[.,]\d+)?)-(\d+(?:[.,]\d+)?)/.exec(value);

  if (matches === null) {
    return $localize`:@@product-wizard.validate.series-number:Has to be a number or range (e.g. 11-20)`;
  }

  const leftNr = parseFloat(matches[1].replace(',', '.'));
  const rightNr = parseFloat(matches[2].replace(',', '.'));

  if (leftNr > rightNr) {
    return $localize`:@@product-wizard.validate.series-number:Has to be a number or range (e.g. 11-20)`;
  }

  return null;
}

export function isInstrumentalConform(value: string): string | null {
  if (value) {
    return $localize`:@@product-wizard.validate.no-lyrics-language-for-instrumental:An instrumental track cannot have a lyrics language`;
  }

  return null;
}
